import type { PlanInfo } from "schemas/billing";
import type { BillingTier } from "schemas/generated/billing";

import { format } from "date-fns";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

export type CancellationStep = "reasons" | "retention" | "confirmation";

export const CANCELLATION_REASONS = [
  { value: "store-closing", label: "I'm closing my business" },
  { value: "too-expensive", label: "It's too expensive" },
  { value: "missing-features", label: "It's missing features I need" },
  { value: "hard-to-learn", label: "It was hard to learn" },
  { value: "found-another-tool", label: "I found another tool" },
  { value: "support-not-helpful", label: "Support wasn't helpful" },
  { value: "not-using-it-enough", label: "I'm not using it enough" },
  { value: "something-else", label: "Something else" },
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number]["value"];

const CANCELLATION_REASON_VALUES = CANCELLATION_REASONS.map(
  (reason) => reason.value,
);

export const CANCELLATION_FEEDBACK_MAX_LENGTH = 5000;

// Each reason asks its own follow-up. The placeholder restates the question instead of showing a sample answer, so it doesn't steer what people write.
// Every cancel needs a sentence of why; "Something else" needs more, since the reason itself says nothing.
const CANCELLATION_FEEDBACK_FIELDS: Record<
  CancellationReason,
  { label: string; placeholder: string; minLength: number }
> = {
  "store-closing": {
    label: "What's changing with your business?",
    placeholder: "Tell us what's changing",
    minLength: 20,
  },
  "too-expensive": {
    label: "What would make the price work for you?",
    placeholder: "Tell us what would work for you",
    minLength: 20,
  },
  "missing-features": {
    label: "Which features did you need?",
    placeholder: "Tell us which features were missing",
    minLength: 20,
  },
  "hard-to-learn": {
    label: "Where did you get stuck?",
    placeholder: "Tell us which part was hard to learn",
    minLength: 20,
  },
  "found-another-tool": {
    label: "Which tool did you pick, and why?",
    placeholder: "Tell us which tool and what made you switch",
    minLength: 20,
  },
  "support-not-helpful": {
    label: "What happened with support?",
    placeholder: "Tell us what happened",
    minLength: 20,
  },
  "not-using-it-enough": {
    label: "What got in the way?",
    placeholder: "Tell us what kept you from using it",
    minLength: 20,
  },
  "something-else": {
    label: "What's the reason?",
    placeholder: "Tell us why you're canceling",
    minLength: 50,
  },
};

export function getCancellationFeedbackField(
  reason: CancellationReason | null | undefined,
): { label: string; placeholder: string } {
  return reason
    ? CANCELLATION_FEEDBACK_FIELDS[reason]
    : {
        label: "Tell us a bit more",
        placeholder: "Choose a reason first",
      };
}

export const cancellationFormSchema = z
  .object({
    reason: z.enum(CANCELLATION_REASON_VALUES, {
      error: "Choose a reason to continue",
    }),
    feedback: z
      .string()
      .max(
        CANCELLATION_FEEDBACK_MAX_LENGTH,
        `Keep this under ${CANCELLATION_FEEDBACK_MAX_LENGTH.toLocaleString()} characters`,
      ),
  })
  .superRefine(({ reason, feedback }, ctx) => {
    const { minLength } = CANCELLATION_FEEDBACK_FIELDS[reason];
    if (feedback.trim().length < minLength) {
      ctx.addIssue({
        path: ["feedback"],
        code: "custom",
        message: `Could you say a bit more? A sentence or two (${minLength} characters or more) helps us make Replo better.`,
      });
    }
  });

export function getCancellationFeedbackHint({
  reason,
  feedback,
}: {
  reason: CancellationReason | null | undefined;
  feedback: string;
}): string | undefined {
  if (!reason) {
    return undefined;
  }
  const remaining =
    CANCELLATION_FEEDBACK_FIELDS[reason].minLength - feedback.trim().length;
  if (remaining <= 0) {
    return undefined;
  }
  return `${remaining} more ${remaining === 1 ? "character" : "characters"} to go`;
}

export type CancellationFormValues = z.infer<typeof cancellationFormSchema>;

export const CANCELLATION_FORM_DEFAULT_VALUES = {
  reason: undefined,
  feedback: "",
} as const;

export type CancellationRetentionOffer = "starterDowngrade" | "agent" | null;

export type CancellationSubmissionLock = {
  isSubmitting: boolean;
};

const starterKeepDefinitions: {
  describe: (maximum: number | null) => string;
  getMaximum: (plan: PlanInfo) => number | null;
}[] = [
  {
    describe: (maximum) => {
      return maximum === null
        ? "Unlimited published items on Shopify"
        : `Up to ${maximum.toLocaleString()} published items on Shopify`;
    },
    getMaximum: (plan) => plan.maxPublishedElements,
  },
  {
    describe: (maximum) => {
      return maximum === 1
        ? "Your Theme Builder project"
        : `${formatAllowance(maximum)} Theme Builder projects`;
    },
    getMaximum: (plan) => plan.maxShopifyThemeProjects,
  },
  {
    describe: (maximum) => `${formatAllowance(maximum)} custom domains`,
    getMaximum: (plan) => plan.maxCustomDomains,
  },
  {
    describe: (maximum) => `${formatAllowance(maximum)} A/B tests`,
    getMaximum: (plan) => plan.maxExperiments,
  },
];

function formatAllowance(maximum: number | null): string {
  return maximum === null ? "Unlimited" : maximum.toLocaleString();
}

// Only entitlements Free strips, so the offer lists what Starter actually preserves.
export function getStarterKeeps({
  starterPlan,
  freePlan,
}: {
  starterPlan: PlanInfo;
  freePlan: PlanInfo;
}): string[] {
  return starterKeepDefinitions.flatMap((definition) => {
    const starterMaximum = definition.getMaximum(starterPlan);
    const freeMaximum = definition.getMaximum(freePlan);
    const keepsMore =
      freeMaximum !== null &&
      (starterMaximum === null || starterMaximum > freeMaximum);
    return keepsMore ? [definition.describe(starterMaximum)] : [];
  });
}

// Mirrors the plan picker: unknown usage is treated as fitting, so a slow query never hides the offer.
export function isUsageWithinPlan({
  plan,
  usage,
}: {
  plan: PlanInfo;
  usage: { publishedElements?: number; themeProjects?: number };
}): boolean {
  const fits = (current: number | undefined, maximum: number | null) => {
    return current === undefined || maximum === null || current <= maximum;
  };
  return (
    fits(usage.publishedElements, plan.maxPublishedElements) &&
    fits(usage.themeProjects, plan.maxShopifyThemeProjects)
  );
}

export function formatCancellationEndDate(
  nextBillingDate: Date | string | null | undefined,
): string {
  const endOfBillingPeriod = "the end of your billing period";

  if (!nextBillingDate) {
    return endOfBillingPeriod;
  }

  const parsedBillingDate = new Date(nextBillingDate);
  if (Number.isNaN(parsedBillingDate.getTime())) {
    return endOfBillingPeriod;
  }

  return format(parsedBillingDate, "MMMM d, yyyy");
}

export function getCancellationRetentionOffer({
  currentTier,
  reason,
  isAgentAvailable,
  isDowngradeAvailable,
}: {
  currentTier: BillingTier;
  reason: CancellationReason | null;
  isAgentAvailable: boolean;
  /** Plan changes on an existing subscription are Shopify-only server-side. */
  isDowngradeAvailable: boolean;
}): CancellationRetentionOffer {
  if (reason === null) {
    return null;
  }

  if (isAgentAvailable) {
    return "agent";
  }

  if (
    isDowngradeAvailable &&
    currentTier === "pro" &&
    reason === "too-expensive"
  ) {
    return "starterDowngrade";
  }

  return null;
}

export function getNextCancellationStep({
  currentStep,
  retentionOffer,
}: {
  currentStep: CancellationStep;
  retentionOffer: CancellationRetentionOffer;
}): CancellationStep | null {
  return exhaustiveSwitch(currentStep)({
    reasons: (): CancellationStep | null => {
      return retentionOffer === null ? "confirmation" : "retention";
    },
    retention: (): CancellationStep | null => "confirmation",
    confirmation: (): CancellationStep | null => null,
  });
}

export async function submitCancellationOnce({
  submissionLock,
  submit,
}: {
  submissionLock: CancellationSubmissionLock;
  submit: () => Promise<void>;
}): Promise<boolean> {
  if (submissionLock.isSubmitting) {
    return false;
  }

  submissionLock.isSubmitting = true;
  try {
    await submit();
    return true;
  } finally {
    submissionLock.isSubmitting = false;
  }
}
